// Document control (rule 37), the SPA's half: the API's types, the words for its codes, and the register form's rules.
// Pure, so it is tested. The type and process lists are the taxonomy Quality keeps, read from the API: none is here.
import type { AuthAction } from '../../lib/types';

/** ⚠ Rule 14: mirrors CK_DocumentVersion_Status (migration 057). */
export type VersionStatus = 'IMPORTED' | 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'EFFECTIVE' | 'SUPERSEDED' | 'WITHDRAWN';
export type ShownStatus = VersionStatus | 'NONE';
export type ReviewState = 'OK' | 'DUE_SOON' | 'OVERDUE';

export const STATUS_LABEL: Record<ShownStatus, string> = {
  IMPORTED: 'Imported, not approved',
  DRAFT: 'Draft',
  IN_REVIEW: 'In review',
  APPROVED: 'Approved, not yet in force',
  EFFECTIVE: 'In force',
  SUPERSEDED: 'Superseded',
  WITHDRAWN: 'Withdrawn',
  NONE: 'No version',
};

export const STATUS_TONE: Record<ShownStatus, 'ok' | 'warn' | 'bad' | 'quiet'> = {
  IMPORTED: 'warn',
  DRAFT: 'quiet',
  IN_REVIEW: 'warn',
  APPROVED: 'quiet',
  EFFECTIVE: 'ok',
  SUPERSEDED: 'quiet',
  WITHDRAWN: 'bad',
  NONE: 'quiet',
};

export const GATE_LABEL: Record<string, string> = {
  readiness: 'Line release (start-up checks)',
  verify: 'Second-person verify',
  first_off: 'First-off check',
  in_process: 'In-process checks',
  goods_in_cofa: 'Goods in: certificate decision',
  iir_vehicle: 'Dock: vehicle inspection',
  iir_goods: 'Dock: goods inspection',
  batch_hold: 'QC Hold and release',
  ncr: 'Non-conformance (NCR)',
  complaint_triage: 'Complaint triage',
  complaint_reply: 'Complaint replies',
  calibration: 'Meter calibration',
};

export const ALIAS_LABEL: Record<string, string> = {
  LEGACY_CODE: 'Old document code',
  FILE_NAME: 'Old file name',
  SHAREPOINT_PATH: 'Old SharePoint path',
  APP_FLOOR: 'Machine SOP in app-floor',
};

/** Who a document can be said to apply to. Mirrors APPLIES_TO_GRANTS (api domain/documents.ts). */
export const APPLIES_TO_GRANTS: readonly AuthAction[] = [
  'CAPTURE',
  'VERIFY',
  'RECEIVE',
  'RELEASE',
  'SPEC_APPROVE',
  'CONCESSION',
  'ASSESS',
  'METER_ADMIN',
  'AUTH_ADMIN',
  'COMPLAINT_TRIAGE',
  'DISPOSITION_RD',
  'DISPOSITION_MFG',
  'DISPOSITION_LOGISTICS',
  'DOC_CONTROL',
  'DOC_APPROVE',
];

export interface DocumentsConfig {
  enabled: boolean;
  siteConfigured: boolean;
  intakeAi: boolean;
  driftAi: boolean;
  gates: string[];
}

export interface TaxonomyType {
  code: string;
  name: string;
  reviewMonths: number;
  sections: string[];
  templateItemId: string | null;
  retired: boolean;
}

export interface TaxonomyProcess {
  code: string;
  name: string;
  retired: boolean;
}

export interface Taxonomy {
  types: TaxonomyType[];
  processes: TaxonomyProcess[];
}

export interface LibraryState {
  syncedAt: string | null;
  lastFailure: { at: string; reason: string } | null;
  configured: boolean;
}

export interface Standing {
  reviewState: ReviewState | null;
  editedOutsideADraft: boolean;
  changedInReview: boolean;
  masterMissing: boolean | null;
}

export interface RegisterEntry {
  id: number;
  docId: string;
  typeCode: string;
  processCode: string;
  title: string;
  ownerRole: string;
  approverRole: string;
  assetId: string | null;
  governs: string[];
  retired: boolean;
  status: ShownStatus;
  versionNo: number | null;
  effectiveFrom: string | null;
  reviewBy: string | null;
  openVersion: { id: number; versionNo: number; status: VersionStatus } | null;
  approvedVersion: { id: number; versionNo: number; effectiveFrom: string | null } | null;
  standing: Standing;
  master: { name: string; webUrl: string | null; modifiedAt: string | null; modifiedByUpn: string | null } | null;
  ack: { applies: number; acknowledged: number; owed: number } | null;
}

export interface LibraryFile {
  itemId: string;
  name: string;
  path: string | null;
  webUrl: string | null;
  modifiedAt: string | null;
  modifiedByUpn: string | null;
}

export interface RegisterPayload {
  documents: RegisterEntry[];
  unregistered: LibraryFile[];
  taxonomy: Taxonomy;
  library: LibraryState;
  needsAttention: number;
}

export interface VersionView {
  id: number;
  versionNo: number;
  status: VersionStatus;
  changeNote: string | null;
  legacyVersion: string | null;
  proposedText: string | null;
  draftedByUpn: string;
  draftedAt: string | null;
  draftVia: string;
  submittedByUpn: string | null;
  submittedAt: string | null;
  masterName: string | null;
  masterModifiedByUpn: string | null;
  warnings: string[];
  returnedByUpn: string | null;
  returnedAt: string | null;
  returnNote: string | null;
  approvedByUpn: string | null;
  approvedAt: string | null;
  approvalNote: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  reviewBy: string | null;
  issuedName: string | null;
  issuedInLibrary: boolean;
  kept: boolean;
  hasText: boolean;
  withdrawnByUpn: string | null;
  withdrawnAt: string | null;
  withdrawNote: string | null;
  acknowledged: number;
}

export interface SuggestionView {
  id: number;
  versionId: number;
  kind: 'DIFF' | 'GATE_DRIFT';
  body: {
    summary?: string;
    changes?: Array<{ section: string; change: string }>;
    disagreements?: Array<{ gate: string; procedureSays: string; disagreement: string }>;
  } | null;
  model: string;
  at: string | null;
}

export interface DocumentDetail extends RegisterEntry {
  reviewerRoles: string[];
  appliesTo: { grants: string[]; upns: string[] };
  createdByUpn: string;
  createdAt: string | null;
  retiredByUpn: string | null;
  retiredAt: string | null;
  retiredReason: string | null;
  masterItemId: string;
  aliases: Array<{ id: number; kind: string; alias: string; byUpn: string; at: string | null }>;
  versions: VersionView[];
  suggestions: SuggestionView[];
  mine: { versionId: number; applies: boolean; acknowledgedAt: string | null } | null;
  people: Array<{ upn: string; acknowledgedAt: string | null }> | null;
  library: LibraryState;
}

export interface MyDocument {
  id: number;
  docId: string;
  typeCode: string;
  processCode: string;
  title: string;
  versionId: number;
  versionNo: number;
  effectiveFrom: string;
  acknowledgedAt: string | null;
}

export interface MinePayload {
  documents: MyDocument[];
  toRead: number;
}

export interface AuditPayload {
  asOf: string;
  documents: Array<{
    id: number;
    docId: string;
    title: string;
    versionNo: number;
    status: VersionStatus;
    effectiveFrom: string;
    effectiveTo: string | null;
    reviewBy: string | null;
    approvedByUpn: string | null;
    approvedAt: string | null;
    issuedSha256: string | null;
    acknowledgedBy: Array<{ upn: string; at: string | null }>;
  }>;
}

/* ── What a row needs someone for ──────────────────────── */

export function attentionOf(d: Pick<RegisterEntry, 'standing' | 'retired' | 'ack'>): string[] {
  if (d.retired) return [];
  const out: string[] = [];
  if (d.standing.reviewState === 'OVERDUE') out.push('Review overdue');
  else if (d.standing.reviewState === 'DUE_SOON') out.push('Review due soon');
  if (d.standing.masterMissing) out.push('Master not in the library');
  if (d.standing.editedOutsideADraft) out.push('Master edited outside a draft');
  if (d.standing.changedInReview) out.push('Master changed since submitted');
  if (d.ack && d.ack.owed > 0) out.push(`${d.ack.owed} to read`);
  return out;
}

/* ── The register form ─────────────────────────────────── */

export interface RegisterForm {
  itemId: string | null;
  fromTemplate: boolean;
  imported: boolean;
  typeCode: string;
  processCode: string;
  title: string;
  ownerRole: string;
  approverRole: string;
  reviewerRoles: string;
  grants: string[];
  upns: string;
  governs: string[];
  assetId: string;
  legacyCode: string;
  legacyVersion: string;
  /** What Claude read, by field. A value still equal to it and not ticked is unchecked: Save waits for it. */
  ai: Record<string, string>;
  confirmed: Record<string, true>;
}

export type AiField = 'title' | 'typeCode' | 'processCode' | 'legacyCode' | 'legacyVersion' | 'ownerRole' | 'approverRole';
export const AI_FIELDS: readonly AiField[] = ['title', 'typeCode', 'processCode', 'legacyCode', 'legacyVersion', 'ownerRole', 'approverRole'];

export interface IntakeSuggestion {
  title: string | null;
  typeCode: string | null;
  processCode: string | null;
  legacyCode: string | null;
  legacyVersion: string | null;
  ownerRole: string | null;
  approverRole: string | null;
}

export function emptyRegisterForm(file: Pick<LibraryFile, 'itemId' | 'name'> | null): RegisterForm {
  return {
    itemId: file?.itemId ?? null,
    fromTemplate: file === null,
    imported: false,
    typeCode: '',
    processCode: '',
    title: file ? file.name.replace(/\.[A-Za-z0-9]{1,10}$/, '') : '',
    ownerRole: '',
    approverRole: '',
    reviewerRoles: '',
    grants: [],
    upns: '',
    governs: [],
    assetId: '',
    legacyCode: '',
    legacyVersion: '',
    ai: {},
    confirmed: {},
  };
}

const split = (s: string) =>
  s
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean);

/** Fill only what is empty: what a person typed is never overwritten. The title counts as empty while it is only the
 *  file's name. */
export function applyIntake(f: RegisterForm, s: IntakeSuggestion, fileTitle: string | null): RegisterForm {
  const next: RegisterForm = { ...f, ai: { ...f.ai } };
  for (const k of AI_FIELDS) {
    const read = s[k];
    if (!read) continue;
    const current = f[k];
    if (current.trim() && !(k === 'title' && current === fileTitle)) continue;
    next[k] = read;
    next.ai[k] = read;
  }
  return next;
}

export const uncheckedAi = (f: RegisterForm) => AI_FIELDS.filter((k) => f.ai[k] !== undefined && f[k] === f.ai[k] && !f.confirmed[k]);

/** Everything that stops Save, each as a sentence. Empty = it can be saved. */
export function registerProblems(f: RegisterForm): string[] {
  const out: string[] = [];
  if (!f.typeCode) out.push('Choose the document type.');
  if (!f.processCode) out.push('Choose the process.');
  if (!f.title.trim()) out.push('Give the document a title.');
  if (!f.ownerRole.trim()) out.push('Name the role that owns it.');
  if (!f.approverRole.trim()) out.push('Name the role that approves it.');
  if (f.ownerRole.trim() && f.ownerRole.trim().toLowerCase() === f.approverRole.trim().toLowerCase()) out.push('The approver role cannot be the owner role.');
  const unchecked = uncheckedAi(f).length;
  if (unchecked) out.push(`${unchecked} value${unchecked === 1 ? '' : 's'} read by AI still to check against the file (tick each, or correct it).`);
  return out;
}

/** The body POST /documents takes, with kept-against-changed per AI value: the measure. */
export function toRegistration(f: RegisterForm) {
  const opt = (s: string) => (s.trim() ? s.trim() : undefined);
  const aiKeys = Object.keys(f.ai) as AiField[];
  return {
    itemId: f.itemId ?? undefined,
    fromTemplate: f.fromTemplate,
    imported: f.imported,
    typeCode: f.typeCode,
    processCode: f.processCode,
    title: f.title.trim(),
    ownerRole: f.ownerRole.trim(),
    approverRole: f.approverRole.trim(),
    reviewerRoles: split(f.reviewerRoles),
    appliesTo: { grants: f.grants, upns: split(f.upns).map((u) => u.toLowerCase()) },
    governs: f.governs,
    assetId: opt(f.assetId),
    legacyCode: opt(f.legacyCode),
    legacyVersion: opt(f.legacyVersion),
    prefill: aiKeys.length ? { checks: aiKeys.map((k) => ({ field: k, ai: f.ai[k], final: f[k] })) } : undefined,
  };
}

/** Today in Brisbane, as a date input wants it. */
export function brisbaneToday(now = new Date()): string {
  return new Date(now.getTime() + 10 * 3_600_000).toISOString().slice(0, 10);
}
